import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Book, AlertCircle, PhoneCall } from 'lucide-react';
import parse from 'html-react-parser';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import { useAuthStore } from '@/features/auth/stores/auth.store';

type DocumentType = 'terms' | 'privacy' | 'disclaimer' | 'contact-consent';

interface DocumentNavItem {
  id: DocumentType;
  title: string;
  icon: JSX.Element;
}

interface PublicLegalDocumentResponse {
  id: string;
  scope: 'broker' | 'platform';
  document_type: string;
  version: string;
  title?: string;
  content: string;
  effective_date: string;
  created_at: string;
  external_url?: string | null;
}

interface BrokerManageDocument {
  id: string;
  user_id: string;
  document_type: string;
  version: string;
  title: string;
  content_text: string;
  external_url: string | null;
  active_flag: boolean;
  effective_date: string;
  created_at: string;
  updated_at: string;
}

const APPLICANT_DOC_TYPES: DocumentNavItem[] = [
  { id: 'terms', title: 'Broker Terms', icon: <FileText className="h-5 w-5" /> },
  { id: 'privacy', title: 'Broker Privacy', icon: <Shield className="h-5 w-5" /> },
  { id: 'disclaimer', title: 'Broker Disclaimer', icon: <Book className="h-5 w-5" /> },
  {
    id: 'contact-consent',
    title: 'Contact Consent',
    icon: <PhoneCall className="h-5 w-5" />,
  },
];

const BROKER_DOC_TYPES: DocumentNavItem[] = [
  { id: 'disclaimer', title: 'Disclaimer', icon: <Book className="h-5 w-5" /> },
  {
    id: 'contact-consent',
    title: 'Contact Consent',
    icon: <PhoneCall className="h-5 w-5" />,
  },
  { id: 'terms', title: 'Terms', icon: <FileText className="h-5 w-5" /> },
  { id: 'privacy', title: 'Privacy', icon: <Shield className="h-5 w-5" /> },
];

const PLATFORM_DOC_TYPES: DocumentNavItem[] = [
  { id: 'terms', title: 'Terms of Service', icon: <FileText className="h-5 w-5" /> },
  { id: 'privacy', title: 'Privacy Policy', icon: <Shield className="h-5 w-5" /> },
];

const normalizeDocumentType = (value?: string): DocumentType => {
  if (value === 'contact-consent') return 'contact-consent';
  if (value === 'privacy') return 'privacy';
  if (value === 'disclaimer') return 'disclaimer';
  return 'terms';
};

const formatDate = (dateString?: string): string => {
  if (!dateString) {
    return 'Unknown date';
  }

  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return dateString;
  }
};

export const LegalPage = () => {
  const { documentType } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthLoading = useAuthStore((state) => state.isLoading);

  const shortId = searchParams.get('shortId') || '';
  const isApplicantMode = Boolean(shortId);
  const isBrokerMode = !isApplicantMode && isAuthenticated;

  const availableDocs = useMemo(() => {
    if (isBrokerMode) {
      return BROKER_DOC_TYPES;
    }

    if (isApplicantMode) {
      return APPLICANT_DOC_TYPES;
    }

    return PLATFORM_DOC_TYPES;
  }, [isApplicantMode, isBrokerMode]);

  const defaultDocType = availableDocs[0]?.id || 'terms';
  const [activeDocument, setActiveDocument] = useState<DocumentType>(
    normalizeDocumentType(documentType || defaultDocType)
  );

  const [publicDocument, setPublicDocument] = useState<PublicLegalDocumentResponse | null>(null);
  const [manageDocuments, setManageDocuments] = useState<BrokerManageDocument[]>([]);
  const [documentVersions, setDocumentVersions] = useState<BrokerManageDocument[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [draftExternalUrl, setDraftExternalUrl] = useState('');
  const [draftEffectiveDate, setDraftEffectiveDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState<string | null>(null);
  const latestLoadRequestRef = useRef(0);

  const activeManageDocument = useMemo(
    () => manageDocuments.find((doc) => doc.document_type === activeDocument),
    [manageDocuments, activeDocument]
  );

  const updateRoute = (docType: DocumentType) => {
    const query = shortId ? `?shortId=${encodeURIComponent(shortId)}` : '';
    // Treat left-nav changes as in-page tab switches, not browser history entries.
    navigate(`/legal/${docType}${query}`, { replace: true });
  };

  const handleGoBack = () => {
    if (isBrokerMode) {
      navigate('/dashboard/settings');
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/');
  };

  const fetchBrokerManageDocuments = async (docType: DocumentType) => {
    const docsResponse = await api.get<{ documents: BrokerManageDocument[] }>('/api/legal/manage/documents');
    const docs = docsResponse.data?.documents || [];

    const versionsResponse = await api.get<{ versions: BrokerManageDocument[] }>(
      `/api/legal/manage/documents/${docType}/versions`
    );
    const versions = versionsResponse.data?.versions || [];

    const active = docs.find((doc) => doc.document_type === docType) || versions.find((doc) => doc.active_flag);

    return {
      docs,
      versions,
      active,
    };
  };

  const applyBrokerManageDocuments = ({
    docs,
    versions,
    active,
  }: {
    docs: BrokerManageDocument[];
    versions: BrokerManageDocument[];
    active?: BrokerManageDocument;
  }) => {
    setManageDocuments(docs);
    setDocumentVersions(versions);

    setDraftTitle(active?.title || '');
    setDraftContent(active?.content_text || '');
    setDraftExternalUrl(active?.external_url || '');
    setDraftEffectiveDate(active?.effective_date ? formatDate(active.effective_date) : formatDate(new Date().toISOString()));
  };

  const fetchPublicDocument = async (docType: DocumentType) => {
    const query = shortId ? `?shortId=${encodeURIComponent(shortId)}` : '';
    const response = await api.get<PublicLegalDocumentResponse>(`/api/legal/documents/${docType}${query}`);
    return response.data;
  };

  useEffect(() => {
    const normalized = normalizeDocumentType(documentType || defaultDocType);
    if (normalized !== activeDocument) {
      setIsLoading(true);
      setError(null);
      setActiveDocument(normalized);
    }
  }, [documentType, defaultDocType, activeDocument]);

  useEffect(() => {
    const requestId = latestLoadRequestRef.current + 1;
    latestLoadRequestRef.current = requestId;
    let cancelled = false;

    const isCurrentRequest = () => !cancelled && latestLoadRequestRef.current === requestId;

    const fetchPageData = async () => {
      if (!activeDocument) return;

      if (!isApplicantMode && !isBrokerMode && isAuthLoading) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        if (isBrokerMode) {
          const brokerPayload = await fetchBrokerManageDocuments(activeDocument);
          if (!isCurrentRequest()) return;
          applyBrokerManageDocuments(brokerPayload);
          setPublicDocument(null);
        } else {
          const document = await fetchPublicDocument(activeDocument);
          if (!isCurrentRequest()) return;
          setPublicDocument(document);
          setManageDocuments([]);
          setDocumentVersions([]);
        }
      } catch (err: any) {
        if (!isCurrentRequest()) return;

        logger.error('Legal', 'Failed loading legal page data', {
          mode: isBrokerMode ? 'broker' : isApplicantMode ? 'applicant' : 'platform-public',
          activeDocument,
          error: err,
          status: err?.response?.status,
          responseData: err?.response?.data,
        });

        if (isBrokerMode && err?.response?.status === 403) {
          setError('Broker legal management is not enabled for this account yet.');
        } else if (err?.response?.data?.error) {
          setError(String(err.response.data.error));
        } else {
          setError('Unable to load legal document. Please try again later.');
        }
      } finally {
        if (isCurrentRequest()) {
          setIsLoading(false);
        }
      }
    };

    fetchPageData();

    return () => {
      cancelled = true;
    };
  }, [activeDocument, isApplicantMode, isBrokerMode, isAuthLoading, shortId]);

  const handleDocumentChange = (docType: DocumentType) => {
    if (docType === activeDocument) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setActiveDocument(docType);
    updateRoute(docType);
  };

  const handleSaveNewVersion = async () => {
    if (!draftContent.trim()) {
      setError('Document content is required.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await api.put(`/api/legal/manage/documents/${activeDocument}`, {
        title: draftTitle,
        contentText: draftContent,
        externalUrl: draftExternalUrl || null,
        effectiveDate: draftEffectiveDate,
      });

      const brokerPayload = await fetchBrokerManageDocuments(activeDocument);
      applyBrokerManageDocuments(brokerPayload);
    } catch (err: any) {
      logger.error('Legal', 'Failed to save broker legal document version', {
        activeDocument,
        error: err,
        status: err?.response?.status,
        responseData: err?.response?.data,
      });

      setError(err?.response?.data?.error || 'Failed to save legal document version.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivateVersion = async (id: string) => {
    setIsActivating(id);
    setError(null);

    try {
      await api.put(`/api/legal/manage/documents/${id}/activate`);
      const brokerPayload = await fetchBrokerManageDocuments(activeDocument);
      applyBrokerManageDocuments(brokerPayload);
    } catch (err: any) {
      logger.error('Legal', 'Failed to activate broker legal document version', {
        documentId: id,
        error: err,
        status: err?.response?.status,
        responseData: err?.response?.data,
      });

      setError(err?.response?.data?.error || 'Failed to activate selected version.');
    } finally {
      setIsActivating(null);
    }
  };

  const activeDocInfo =
    availableDocs.find((doc) => doc.id === activeDocument) ||
    availableDocs[0] || {
      id: 'terms' as DocumentType,
      title: 'Legal',
      icon: <FileText className="h-5 w-5" />,
    };

  const renderPublicContent = () => {
    if (!publicDocument) return null;

    return (
      <>
        <div className="mt-2 sm:mt-0 text-sm text-gray-500">
          {publicDocument.version} • Last updated: {formatDate(publicDocument.effective_date)}
        </div>
        <div className="prose max-w-none mt-6">{parse(publicDocument.content || '')}</div>
      </>
    );
  };

  const renderBrokerEditor = () => {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              placeholder="Document title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
            <input
              type="date"
              value={draftEffectiveDate}
              onChange={(e) => setDraftEffectiveDate(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">External URL (optional)</label>
          <input
            type="url"
            value={draftExternalUrl}
            onChange={(e) => setDraftExternalUrl(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="https://example.com/legal"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Document Content</label>
          <textarea
            value={draftContent}
            onChange={(e) => setDraftContent(e.target.value)}
            rows={16}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono"
            placeholder="Enter legal content text"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSaveNewVersion}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save New Version'}
          </button>
          {activeManageDocument && (
            <div className="text-sm text-gray-500">
              Active version: {activeManageDocument.version} ({formatDate(activeManageDocument.effective_date)})
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Version History</h3>
          <div className="space-y-2">
            {documentVersions.map((version) => (
              <div
                key={version.id}
                className="border border-gray-200 rounded p-3 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium text-sm text-gray-900">
                    {version.version} • {formatDate(version.effective_date)}
                  </div>
                  <div className="text-xs text-gray-500">{version.title}</div>
                </div>
                <button
                  type="button"
                  onClick={() => handleActivateVersion(version.id)}
                  disabled={version.active_flag || isActivating === version.id}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${
                    version.active_flag
                      ? 'bg-green-100 text-green-700 cursor-default'
                      : 'bg-gray-800 text-white hover:bg-gray-900'
                  } disabled:opacity-60`}
                >
                  {version.active_flag
                    ? 'Active'
                    : isActivating === version.id
                      ? 'Activating...'
                      : 'Activate'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-gray-800 text-white p-4">
        <div className="container mx-auto flex items-center">
          <button
            onClick={handleGoBack}
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </button>
          <h1 className="text-xl font-semibold ml-4">Legal</h1>
        </div>
      </header>

      <div className="container mx-auto py-8 px-4">
        <div className="flex flex-col md:flex-row">
          <aside className="w-full md:w-64 mb-6 md:mb-0">
            <div className="bg-gray-50 rounded-lg p-4 sticky top-4">
              <h2 className="text-lg font-semibold mb-4">
                {isBrokerMode ? 'Broker Legal Documents' : 'Legal Documents'}
              </h2>
              <nav className="space-y-1">
                {availableDocs.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => handleDocumentChange(doc.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm rounded-md ${
                      activeDocument === doc.id
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {doc.icon}
                    <span className="ml-3">{doc.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          <main className="flex-1 md:ml-8">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                <h1 className="text-2xl font-bold">{activeDocInfo.title}</h1>
              </div>

              {isLoading && (
                <div className="min-h-[320px] flex flex-col items-center justify-center text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mb-2"></div>
                  <p className="text-gray-600">Loading document...</p>
                </div>
              )}

              {!isLoading && error && (
                <div className="py-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-4 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {!isLoading && !error && isBrokerMode && renderBrokerEditor()}
              {!isLoading && !error && !isBrokerMode && renderPublicContent()}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};
